import {
  PdfGen,
  PdfUnit,
  PdfRect,
  PdfPoint,
  PdfPen,
  PdfColor,
  PdfBrush,
  PdfFont,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutBaseGen } from '../layoutBase';
import { OptsBase } from '../optsBase';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import { GUtil } from '../../pdf/gutil';
import { Random } from '@educraft/base';
import { GLabel, GTuple } from '../../pdf/glabel';
import {
  ELogicoVariant,
  ELogicoMode,
  genMap,
  drawLogico,
  logicoColors,
} from '../../pdf/board/logico';
import type { LogicoData } from '../../pdf/board/logico';

// ===== Interval =====

export class Interval {
  constructor(
    public readonly min: number,
    public readonly max: number,
    public readonly minInclusive = true,
    public readonly maxInclusive = true
  ) {}

  public contains(x: number): boolean {
    const left = this.minInclusive ? x >= this.min : x > this.min;
    const right = this.maxInclusive ? x <= this.max : x < this.max;
    return left && right;
  }

  public static readonly All = new Interval(-Infinity, Infinity);

  public static Open(min: number, max: number): Interval {
    return new Interval(min, max, false, false);
  }
  public static Closed(min: number, max: number): Interval {
    return new Interval(min, max, true, true);
  }
  public static FromClosed(value: number): Interval {
    return new Interval(value, Infinity, true, false);
  }
  public static FromOpen(value: number): Interval {
    return new Interval(value, Infinity, false, false);
  }

  public static Minus(...values: number[]): Interval[] {
    if (values.length === 0) return [Interval.Open(-Infinity, Infinity)];
    const sorted = [...values].sort((a, b) => a - b);
    const list: Interval[] = [Interval.Open(-Infinity, sorted[0])];
    for (let i = 0; i < sorted.length - 1; i++) {
      list.push(Interval.Open(sorted[i], sorted[i + 1]));
    }
    list.push(Interval.Open(sorted[sorted.length - 1], Infinity));
    return list;
  }
}

// ===== MathFunction =====

export class MathFunction {
  public readonly name: string;
  public readonly func: (x: number) => number;
  public readonly domain: Interval[];

  constructor(name: string, func: (x: number) => number, ...domain: Interval[]) {
    this.name = name;
    this.func = func;
    this.domain = domain.length === 0 ? [Interval.All] : domain;
  }

  public isDefinedAt(x: number): boolean {
    return this.domain.some((i) => i.contains(x));
  }

  public hasDomainGapBetween(x1: number, x2: number): boolean {
    if (x1 === x2) return false;
    const min = Math.min(x1, x2);
    const max = Math.max(x1, x2);
    const relevant = this.domain
      .filter((i) => i.max >= min && i.min <= max)
      .sort((a, b) => a.min - b.min);
    if (relevant.length === 0) return true;
    let current = min;
    for (const interval of relevant) {
      const gapBefore =
        interval.min > current || (interval.min === current && !interval.minInclusive);
      if (gapBefore) return true;
      if (interval.max > current || (interval.max === current && interval.maxInclusive)) {
        current = interval.max;
      }
    }
    return current < max;
  }
}

// ===== PiccoloFunctionBase =====

export abstract class PiccoloFunctionBase implements ILayoutBaseGen {
  constructor(protected readonly variant: ELogicoVariant) {}

  public abstract getFunctions(): MathFunction[];
  public get minX(): number {
    return -5;
  }
  public get maxX(): number {
    return 5;
  }
  public get step(): number {
    return 0.1;
  }

  public async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    pdf.addPage();
    pdf.addPage();

    const random = opts.data?.random ?? new Random();
    const list = this.getFunctions();
    const labels: GTuple[] = list.map((f) => ({
      left: GLabel.fromText(f.name),
      right: GLabel.fromText(f.name),
    }));

    let map = genMap(list.length, random);
    if (this.variant === ELogicoVariant.Piccolo) {
      map = map.map((i) => i * 2);
    }
    const colors = logicoColors(this.variant);
    const {minX} = this;
    const {maxX} = this;
    const {step} = this;
    const diffX = maxX - minX;

    const drawQuestion = (data: LogicoData) => {
      const {rect} = data;
      const top = ((rect.height / rect.width) * diffX) / 2;
      const maxY = top;
      const minY = -top;
      const diffY = diffX; // same as 2*top = height/width * diffX, but let's keep it as diffX for square aspect

      // Coordinate → PDF point (Y flipped: math y=0 at bottom, PDF y=0 at top)
      const P = (x: number, y: number): PdfPoint =>
        new PdfPoint(
          rect.x + ((x - minX) / diffX) * rect.width,
          rect.bottom - ((y - minY) / (maxY - minY)) * rect.height
        );

      const c1X = Math.ceil(minX);
      const c2X = Math.floor(maxX);
      const c1Y = Math.ceil(minY);
      const c2Y = Math.floor(maxY);

      // Dashed background grid
      const backPen = new PdfPen(PdfColor.fromRgb(128, 128, 128), 0.5);
      backPen.dashArray = [2, 2];

      for (let x = c1X; x <= c2X; x++) {
        data.page.drawLine(backPen, P(x, minY), P(x, maxY));
      }
      for (let y = c1Y; y <= c2Y; y++) {
        data.page.drawLine(backPen, P(minX, y), P(maxX, y));
      }

      // Axis labels (font size 8)
      const f = new PdfFont(PdfFont.FONT_NAME, 8);
      const lHalfW = PdfUnit.FromMillimeters(5);
      const lH = PdfUnit.FromMillimeters(4);
      const axis0 = P(0, 0);

      for (let x = c1X; x <= c2X; x++) {
        if (x === 0) continue;
        const p = P(x, 0);
        data.page.drawString(
          `${x}`,
          f,
          PdfBrush.Black,
          null,
          new PdfRect(p.x - lHalfW, p.y, lHalfW * 2, lH),
          ETextAlign.TopCenter
        );
      }
      for (let y = c1Y; y <= c2Y; y++) {
        if (y === 0) continue;
        const p = P(0, y);
        data.page.drawString(
          `${y}`,
          f,
          PdfBrush.Black,
          null,
          new PdfRect(axis0.x - lHalfW * 2, p.y - lH / 2, lHalfW * 2, lH),
          ETextAlign.CenterRight
        );
      }

      // Axes (solid)
      data.page.drawLine(GUtil.basePen, P(minX, 0), P(maxX, 0));
      data.page.drawLine(GUtil.basePen, P(0, minY), P(0, maxY));

      // Function curves
      for (let i = 0; i < list.length; i++) {
        const fun = list[data.invMap[i]];
        let pp: PdfPoint | null = null;
        let xp = minX;
        const pen = new PdfPen(colors[i % colors.length], 3);

        for (let x = minX; x <= maxX + step * 0.5; x += step) {
          if (!fun.isDefinedAt(x)) {
            pp = null;
            xp = x;
            continue;
          }

          const yRaw = fun.func(x);
          const yClamped = Math.max(minY, Math.min(maxY, yRaw));
          const p = P(x, yClamped);

          if (fun.hasDomainGapBetween(xp, x)) {
            pp = null;
          }

          if (pp !== null) {
            data.page.drawLine(pen, pp, p);
          }

          pp = yRaw > maxY || yRaw < minY ? null : p;
          xp = x;
        }
      }

      // Endpoint circles at x = minX and x = maxX
      const eSize = PdfUnit.FromMillimeters(3);
      for (let i = 0; i < list.length; i++) {
        const fun = list[data.invMap[i]];
        const color = colors[i % colors.length];

        const drawEndpoint = (x: number) => {
          if (!fun.isDefinedAt(x)) return;
          const y = fun.func(x);
          if (y < minY || y > maxY) return;
          const p = P(x, y);
          const er = new PdfRect(p.x - eSize / 2, p.y - eSize / 2, eSize, eSize);
          data.page.drawEllipse(null, new PdfBrush(color), er);
          data.page.drawEllipse(GUtil.basePen, null, er);
        };

        drawEndpoint(minX);
        drawEndpoint(maxX);
      }
    };

    drawLogico(pdf, this.variant, ELogicoMode.One, labels, map, {
      id: this.constructor.name,
      drawQuestion,
      drawOutline: false,
      drawCircle: false,
      singleLineAnswers: true,
      data: opts.data,
    });
  }
}

// ===== Piccolo generators =====

export class Piccolo_Fun__Sin extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Piccolo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('sin(x)', (x) => Math.sin(x)),
      new MathFunction('sin(x) + 1', (x) => Math.sin(x) + 1),
      new MathFunction('sin(x \u2212 1)', (x) => Math.sin(x - 1)),
      new MathFunction('sin(\u2212x)', (x) => Math.sin(-x)),
      new MathFunction('2 · sin(x)', (x) => 2 * Math.sin(x)),
    ];
  }
}

export class Piccolo_Fun__Cos extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Piccolo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('cos(x)', (x) => Math.cos(x)),
      new MathFunction('cos(x) + 1', (x) => Math.cos(x) + 1),
      new MathFunction('cos(x \u2212 1)', (x) => Math.cos(x - 1)),
      new MathFunction('\u2212cos(x)', (x) => -Math.cos(x)),
      new MathFunction('2 · cos(x)', (x) => 2 * Math.cos(x)),
    ];
  }
}

export class Piccolo_Fun__Linear extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Piccolo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('x', (x) => x),
      new MathFunction('x + 1', (x) => x + 1),
      new MathFunction('x \u2212 1', (x) => x - 1),
      new MathFunction('\u2212x', (x) => -x),
      new MathFunction('2 · x', (x) => 2 * x),
    ];
  }
}

export class Piccolo_Fun__Abs extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Piccolo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('|x|', (x) => Math.abs(x)),
      new MathFunction('\u2212|x|', (x) => -Math.abs(x)),
      new MathFunction('|2 · x|', (x) => Math.abs(2 * x)),
      new MathFunction('|x \u2212 2|', (x) => Math.abs(x - 2)),
      new MathFunction('|x| + 1', (x) => Math.abs(x) + 1),
    ];
  }
}

export class Piccolo_Fun__1X extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Piccolo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('1 / x', (x) => 1 / x, ...Interval.Minus(0)),
      new MathFunction('4 / x', (x) => 4 / x, ...Interval.Minus(0)),
      new MathFunction('\u2212 1 / x', (x) => -1 / x, ...Interval.Minus(0)),
      new MathFunction('1 + 1 / x', (x) => 1 + 1 / x, ...Interval.Minus(0)),
      new MathFunction('1 / (x \u2212 2)', (x) => 1 / (x - 2), ...Interval.Minus(1)),
    ];
  }
  get step(): number {
    return 0.05;
  }
}

export class Piccolo_Fun__Sqrt extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Piccolo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('sqrt(x)', (x) => Math.sqrt(x), Interval.FromClosed(0)),
      new MathFunction('sqrt(2 · x)', (x) => Math.sqrt(2 * x), Interval.FromClosed(0)),
      new MathFunction('2 · sqrt(x)', (x) => 2 * Math.sqrt(x), Interval.FromClosed(0)),
      new MathFunction('sqrt(x) \u2212 1', (x) => Math.sqrt(x) - 1, Interval.FromClosed(0)),
      new MathFunction('sqrt(x + 1)', (x) => Math.sqrt(x + 1), Interval.FromClosed(-1)),
    ];
  }
  get step(): number {
    return 0.01;
  }
}

export class Piccolo_Fun__X2 extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Piccolo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('x\u00b2', (x) => x * x),
      new MathFunction('x\u00b2 - 1', (x) => x * x - 1),
      new MathFunction('(x + 1)\u00b2', (x) => (x + 1) * (x + 1)),
      new MathFunction('\u2212x\u00b2', (x) => -x * x),
      new MathFunction('(x \u2212 1)\u00b2', (x) => (x - 1) * (x - 1)),
    ];
  }
}

export class Piccolo_Fun__X3 extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Piccolo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('x\u00b3', (x) => x * x * x),
      new MathFunction('x\u00b3 - 1', (x) => x * x * x - 1),
      new MathFunction('(x + 1)\u00b3', (x) => (x + 1) * (x + 1) * (x + 1)),
      new MathFunction('\u2212x\u00b3', (x) => -x * x * x),
      new MathFunction('(x \u2212 1)\u00b3', (x) => (x - 1) * (x - 1) * (x - 1)),
    ];
  }
}

export class Piccolo_Fun__Log extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Piccolo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('ln(x)', (x) => Math.log(x), Interval.FromOpen(0)),
      new MathFunction('ln(x - 1)', (x) => Math.log(x - 1), Interval.FromOpen(1)),
      new MathFunction('ln(x) - 1', (x) => Math.log(x) - 1, Interval.FromOpen(0)),
      new MathFunction('-ln(x)', (x) => -Math.log(x), Interval.FromOpen(0)),
      new MathFunction('ln(x + 2)', (x) => Math.log(x + 2), Interval.FromOpen(-2)),
    ];
  }
  get step(): number {
    return 0.01;
  }
}

export class Piccolo_Fun__Exp extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Piccolo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('e\u02e3', (x) => Math.exp(x)),
      new MathFunction('\u2212 e\u02e3', (x) => -Math.exp(x)),
      new MathFunction('e\u02e3\u207b\u00b9', (x) => Math.exp(x - 1)),
      new MathFunction('e\u02e3\u207a\u00b9', (x) => Math.exp(x + 1)),
      new MathFunction('e\u02e3 \u2212 1', (x) => Math.exp(x) - 1),
    ];
  }
  get step(): number {
    return 0.01;
  }
}

// ===== Primo generators =====

export class Primo_Fun__Sin extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Primo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('sin(x)', (x) => Math.sin(x)),
      new MathFunction('sin(x) + 1', (x) => Math.sin(x) + 1),
      new MathFunction('sin(x \u2212 1)', (x) => Math.sin(x - 1)),
      new MathFunction('sin(\u2212x)', (x) => Math.sin(-x)),
      new MathFunction('2 · sin(x)', (x) => 2 * Math.sin(x)),
      new MathFunction('sin(2 · x)', (x) => Math.sin(2 * x)),
    ];
  }
}

export class Primo_Fun__Cos extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Primo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('cos(x)', (x) => Math.cos(x)),
      new MathFunction('cos(x) + 1', (x) => Math.cos(x) + 1),
      new MathFunction('cos(x \u2212 1)', (x) => Math.cos(x - 1)),
      new MathFunction('\u2212cos(x)', (x) => -Math.cos(x)),
      new MathFunction('2 · cos(x)', (x) => 2 * Math.cos(x)),
      new MathFunction('cos(2 · x)', (x) => Math.cos(2 * x)),
    ];
  }
}

export class Primo_Fun__Linear extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Primo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('x', (x) => x),
      new MathFunction('x + 1', (x) => x + 1),
      new MathFunction('x \u2212 1', (x) => x - 1),
      new MathFunction('\u2212x', (x) => -x),
      new MathFunction('2 · x', (x) => 2 * x),
      new MathFunction('x : 2', (x) => x / 2),
    ];
  }
}

export class Primo_Fun__Abs extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Primo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('|x|', (x) => Math.abs(x)),
      new MathFunction('\u2212|x|', (x) => -Math.abs(x)),
      new MathFunction('|2 · x|', (x) => Math.abs(2 * x)),
      new MathFunction('|x \u2212 2|', (x) => Math.abs(x - 2)),
      new MathFunction('|x| + 1', (x) => Math.abs(x) + 1),
      new MathFunction('|x : 2|', (x) => Math.abs(x / 2)),
    ];
  }
}

export class Primo_Fun__1X extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Primo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('1 / x', (x) => 1 / x, ...Interval.Minus(0)),
      new MathFunction('4 / x', (x) => 4 / x, ...Interval.Minus(0)),
      new MathFunction('\u2212 1 / x', (x) => -1 / x, ...Interval.Minus(0)),
      new MathFunction('1 + 1 / x', (x) => 1 + 1 / x, ...Interval.Minus(0)),
      new MathFunction('1 / (x \u2212 2)', (x) => 1 / (x - 2), ...Interval.Minus(1)),
      new MathFunction('1 / 2x', (x) => 1 / (2 * x), ...Interval.Minus(0)),
    ];
  }
  get step(): number {
    return 0.05;
  }
}

export class Primo_Fun__Sqrt extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Primo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('sqrt(x)', (x) => Math.sqrt(x), Interval.FromClosed(0)),
      new MathFunction('sqrt(2 · x)', (x) => Math.sqrt(2 * x), Interval.FromClosed(0)),
      new MathFunction('2 · sqrt(x)', (x) => 2 * Math.sqrt(x), Interval.FromClosed(0)),
      new MathFunction('sqrt(x) \u2212 1', (x) => Math.sqrt(x) - 1, Interval.FromClosed(0)),
      new MathFunction('sqrt(x + 1)', (x) => Math.sqrt(x + 1), Interval.FromClosed(-1)),
      new MathFunction('sqrt(x : 2)', (x) => Math.sqrt(x / 2), Interval.FromClosed(0)),
    ];
  }
  get step(): number {
    return 0.01;
  }
}

export class Primo_Fun__X2 extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Primo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('x\u00b2', (x) => x * x),
      new MathFunction('x\u00b2 - 1', (x) => x * x - 1),
      new MathFunction('(x + 1)\u00b2', (x) => (x + 1) * (x + 1)),
      new MathFunction('\u2212x\u00b2', (x) => -x * x),
      new MathFunction('(x \u2212 1)\u00b2', (x) => (x - 1) * (x - 1)),
      new MathFunction('x\u00b2 : 2', (x) => (x * x) / 2),
    ];
  }
}

export class Primo_Fun__X3 extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Primo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('x\u00b3', (x) => x * x * x),
      new MathFunction('x\u00b3 - 1', (x) => x * x * x - 1),
      new MathFunction('(x + 1)\u00b3', (x) => (x + 1) * (x + 1) * (x + 1)),
      new MathFunction('\u2212x\u00b3', (x) => -x * x * x),
      new MathFunction('(x \u2212 1)\u00b3', (x) => (x - 1) * (x - 1) * (x - 1)),
      new MathFunction('x\u00b3 : 2', (x) => (x * x * x) / 2),
    ];
  }
}

export class Primo_Fun__Log extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Primo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('ln(x)', (x) => Math.log(x), Interval.FromOpen(0)),
      new MathFunction('ln(x - 1)', (x) => Math.log(x - 1), Interval.FromOpen(1)),
      new MathFunction('ln(x) - 1', (x) => Math.log(x) - 1, Interval.FromOpen(0)),
      new MathFunction('-ln(x)', (x) => -Math.log(x), Interval.FromOpen(0)),
      new MathFunction('ln(x + 2)', (x) => Math.log(x + 2), Interval.FromOpen(-2)),
      new MathFunction('2 · ln(x)', (x) => 2 * Math.log(x), Interval.FromOpen(0)),
    ];
  }
  get step(): number {
    return 0.01;
  }
}

export class Primo_Fun__Exp extends PiccoloFunctionBase {
  constructor() {
    super(ELogicoVariant.Primo);
  }
  getFunctions(): MathFunction[] {
    return [
      new MathFunction('e\u02e3', (x) => Math.exp(x)),
      new MathFunction('\u2212 e\u02e3', (x) => -Math.exp(x)),
      new MathFunction('e\u02e3\u207b\u00b9', (x) => Math.exp(x - 1)),
      new MathFunction('e\u02e3\u207a\u00b9', (x) => Math.exp(x + 1)),
      new MathFunction('e\u02e3 \u2212 1', (x) => Math.exp(x) - 1),
      new MathFunction('2 · e\u02e3', (x) => 2 * Math.exp(x)),
    ];
  }
  get step(): number {
    return 0.01;
  }
}
